import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

/**
 * Org-admin self-serve OIDC SSO configuration (P1). Generic across IdPs — the admin pastes their
 * issuer / client id / secret and the email domains that should federate. Client secret is
 * write-only (never returned; `secretSet` shows whether one is stored).
 */
export default function SsoSettings() {
  const { t } = useTranslation();
  const blank = {
    enabled: false, providerLabel: '', issuerUri: '', clientId: '', clientSecret: '',
    scopes: 'openid profile email', groupsClaim: 'groups', adminGroup: '', emailDomains: [],
  };
  const [form, setForm] = useState(blank);
  const [secretSet, setSecretSet] = useState(false);
  const [domainsText, setDomainsText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [testMsg, setTestMsg] = useState('');
  const [suggestion, setSuggestion] = useState(null);   // existing Microsoft connector app, if any
  const [reuseSecret, setReuseSecret] = useState(false); // reuse the connector's stored secret

  // The URL the customer's IdP admin must register as the app's redirect/callback URI.
  const redirectUri = (() => {
    try { return new URL(process.env.REACT_APP_API_BASE_URL).origin + '/api/sso/callback'; }
    catch { return '/api/sso/callback'; }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/admin/sso-config');
      setForm({ ...blank, ...data, clientSecret: '' });
      setSecretSet(!!data.secretSet);
      setDomainsText((data.emailDomains || []).join(', '));
      // Offer to reuse an already-connected Microsoft (M365/Azure) app instead of a second one.
      const sug = await axiosInstance.get('/admin/sso-config/microsoft-suggestion').then(r => r.data).catch(() => null);
      if (sug && sug.available) setSuggestion(sug);
    } catch {
      setMsg(t('Sso.loadError', 'Could not load SSO settings.'));
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const parseDomains = () => domainsText.split(/[\s,]+/).map(d => d.trim().toLowerCase()).filter(Boolean);

  const useMicrosoftApp = () => {
    if (!suggestion) return;
    setForm(f => ({
      ...f,
      providerLabel: f.providerLabel || 'Microsoft',
      issuerUri: suggestion.issuerUri,
      clientId: suggestion.clientId,
    }));
    if (suggestion.secretAvailable) setReuseSecret(true);
    setMsg(t('Sso.reuseApplied', 'Pre-filled from your Microsoft connector. Add the redirect URI to that app in Entra, then Save.'));
  };

  const save = async () => {
    setSaving(true); setMsg('');
    const payload = { ...form, emailDomains: parseDomains(), reuseConnectorSecret: reuseSecret };
    if (!payload.clientSecret) delete payload.clientSecret;   // keep existing / reuse connector secret when blank
    try {
      const { data } = await axiosInstance.put('/admin/sso-config', payload);
      setForm({ ...blank, ...data, clientSecret: '' });
      setSecretSet(!!data.secretSet);
      setDomainsText((data.emailDomains || []).join(', '));
      setMsg(t('Sso.saved', 'SSO settings saved.'));
    } catch (e) {
      const m = e.response?.status === 409
        ? t('Sso.domainConflict', 'One of those email domains is already claimed by another organization.')
        : (e.response?.data?.message || t('Sso.saveError', 'Could not save SSO settings.'));
      setMsg(m);
    } finally { setSaving(false); }
  };

  const testIssuer = async () => {
    setTestMsg(t('Sso.testing', 'Checking…'));
    try {
      const { data } = await axiosInstance.post('/admin/sso-config/test', { issuerUri: form.issuerUri });
      setTestMsg(data.ok
        ? t('Sso.testOk', '✓ Issuer looks good — discovery succeeded.')
        : `✗ ${data.error}`);
    } catch { setTestMsg(t('Sso.testError', '✗ Could not reach that issuer.')); }
  };

  const removeConfig = async () => {
    if (!window.confirm(t('Sso.confirmDelete', 'Remove SSO for this organization? Users will fall back to password login.'))) return;
    setSaving(true);
    try { await axiosInstance.delete('/admin/sso-config'); setForm(blank); setSecretSet(false); setDomainsText(''); setMsg(t('Sso.removed', 'SSO removed.')); }
    catch { setMsg(t('Sso.saveError', 'Could not remove SSO settings.')); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="settings-section"><p>{t('Settings.loading', 'Loading…')}</p></div>;

  const label = { display: 'block', fontSize: '0.8rem', color: '#475569', marginTop: '0.9rem', marginBottom: '0.25rem' };
  const input = { width: '100%', padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem' };

  return (
    <div className="settings-section" style={{ maxWidth: 640 }}>
      <div className="settings-header"><h2>{t('Sso.title', 'Single Sign-On (OIDC)')}</h2></div>
      <p style={{ fontSize: '0.875rem', color: '#555' }}>
        {t('Sso.intro', 'Let your team sign in with their corporate identity (Microsoft Entra, Google Workspace, Okta, …). Register the redirect URI below in your IdP, then paste the issuer and client credentials here.')}
      </p>

      {suggestion && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '0.7rem 0.9rem', margin: '0.75rem 0', fontSize: '0.85rem' }}>
          <div style={{ fontWeight: 600, color: '#1e40af' }}>
            🔗 {t('Sso.reuseTitle', 'Reuse your existing Microsoft app')}
          </div>
          <p style={{ margin: '0.35rem 0', color: '#334155' }}>
            {t('Sso.reuseBody', `You already connected Microsoft via your ${suggestion.source} integration (tenant ${suggestion.tenantId}). Reuse that same Entra app for SSO — no need to register a second one.`)}
          </p>
          <button type="button" onClick={useMicrosoftApp} style={{ background: '#2563eb', color: '#fff' }}>
            {t('Sso.reuseUse', 'Use this app')}
          </button>
          <div style={{ marginTop: 6, color: '#64748b', fontSize: '0.8rem' }}>
            {t('Sso.reuseHint', 'One step in Entra: add the redirect URI below to that app’s Authentication → Web.')}
          </div>
        </div>
      )}

      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.6rem 0.8rem', margin: '0.75rem 0', fontSize: '0.82rem' }}>
        <strong>{t('Sso.redirectUri', 'Redirect / callback URI')}</strong>
        <div style={{ fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 4 }}>{redirectUri}</div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.5rem' }}>
        <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
        <span>{t('Sso.enabled', 'SSO enabled for this organization')}</span>
      </label>

      <label style={label}>{t('Sso.providerLabel', 'Provider name (shown on the login button)')}</label>
      <input style={input} value={form.providerLabel || ''} placeholder="Microsoft" onChange={e => set('providerLabel', e.target.value)} />

      <label style={label}>{t('Sso.issuer', 'Issuer URL')}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={input} value={form.issuerUri || ''} placeholder="https://login.microsoftonline.com/<tenant>/v2.0" onChange={e => set('issuerUri', e.target.value)} />
        <button type="button" onClick={testIssuer} disabled={!form.issuerUri} style={{ whiteSpace: 'nowrap' }}>{t('Sso.test', 'Test')}</button>
      </div>
      {testMsg && <div style={{ fontSize: '0.8rem', marginTop: 4, color: testMsg.startsWith('✓') ? '#15803d' : '#b45309' }}>{testMsg}</div>}

      <label style={label}>{t('Sso.clientId', 'Client ID')}</label>
      <input style={input} value={form.clientId || ''} onChange={e => set('clientId', e.target.value)} />

      <label style={label}>{t('Sso.clientSecret', 'Client secret')}</label>
      <input style={input} type="password" value={form.clientSecret || ''}
        placeholder={reuseSecret
          ? t('Sso.secretReused', 'Reusing your Microsoft connector’s secret — leave blank')
          : (secretSet ? t('Sso.secretStored', '•••••••• (stored — leave blank to keep)') : '')}
        onChange={e => { set('clientSecret', e.target.value); if (e.target.value) setReuseSecret(false); }} />
      {reuseSecret && (
        <div style={{ fontSize: '0.78rem', color: '#2563eb', marginTop: 4 }}>
          {t('Sso.secretReusedNote', '↺ Using the secret from your Microsoft connector. Type a value to override.')}
        </div>
      )}

      <label style={label}>{t('Sso.emailDomains', 'Email domains (comma-separated) that use this IdP')}</label>
      <input style={input} value={domainsText} placeholder="acme.com, acme.co.uk" onChange={e => setDomainsText(e.target.value)} />

      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{ flex: 1 }}>
          <label style={label}>{t('Sso.groupsClaim', 'Groups claim')}</label>
          <input style={input} value={form.groupsClaim || ''} placeholder="groups" onChange={e => set('groupsClaim', e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={label}>{t('Sso.adminGroup', 'Admin group (optional)')}</label>
          <input style={input} value={form.adminGroup || ''} placeholder="cataam-admins" onChange={e => set('adminGroup', e.target.value)} />
        </div>
      </div>

      <label style={label}>{t('Sso.scopes', 'Scopes')}</label>
      <input style={input} value={form.scopes || ''} onChange={e => set('scopes', e.target.value)} />

      {msg && <p style={{ marginTop: '0.9rem', fontSize: '0.85rem', color: msg.includes('saved') || msg.includes('removed') ? '#15803d' : '#b91c1c' }}>{msg}</p>}

      <div style={{ display: 'flex', gap: 12, marginTop: '1.1rem' }}>
        <button type="button" onClick={save} disabled={saving}>
          {saving ? t('Settings.saving', 'Saving…') : <><i className="fas fa-save"></i> {t('Sso.save', 'Save SSO settings')}</>}
        </button>
        {form.id && (
          <button type="button" onClick={removeConfig} disabled={saving}
            style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fecaca' }}>
            {t('Sso.remove', 'Remove')}
          </button>
        )}
      </div>
    </div>
  );
}
